import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { WorkSecurityArea } from "services/work-security-areas.service";
import {
  assignUsersToRole,
  fetchWorkSecurityRoles,
  WorkSecurityRole,
} from "services/work-security.service";
import GroupedRoleCheckboxes from "features/positions/GroupedRoleCheckboxes";
import { describePositionsError } from "features/positions/positions-errors";

/**
 * «+ Pridėti individualiai» on the employee card: assign pareigybės of the
 * spheres the current user manages directly to this employee. Individual
 * assignments need no approval and stay until removed by hand; position
 * changes never touch them (positions catalogue §1 rule 6).
 */
type Props = {
  companyId: string;
  userId: string;
  managedAreas: WorkSecurityArea[];
  /** Roles already held individually — not offered again. */
  individualRoleIds: string[];
  onChanged: () => void;
};

export default function IndividualRoleAdd({
  companyId,
  userId,
  managedAreas,
  individualRoleIds,
  onChanged,
}: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [roles, setRoles] = useState<WorkSecurityRole[] | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setRoles(null);
    fetchWorkSecurityRoles(companyId)
      .then((rows) => {
        if (!cancelled) setRoles(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {
        if (!cancelled) setRoles([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, companyId]);

  const groups = useMemo(() => {
    const held = new Set(individualRoleIds);
    return managedAreas
      .map((area) => ({
        area,
        roles: (roles || [])
          .filter((role) => role.area === area.id && !role.isArchived && !held.has(role.id))
          .sort((a, b) => a.name.localeCompare(b.name, "lt")),
      }))
      .filter((group) => group.roles.length > 0);
  }, [managedAreas, roles, individualRoleIds]);

  if (!managedAreas.length) return null;

  const save = async () => {
    setSaving(true);
    try {
      // One request per role — the existing assign endpoint is per role.
      for (const roleId of selected) {
        // eslint-disable-next-line no-await-in-loop
        await assignUsersToRole(companyId, roleId, [userId]);
      }
      toast.success(t("ws_individual_added"));
      setSelected([]);
      setOpen(false);
    } catch (e: any) {
      toast.error(describePositionsError(e, t));
    } finally {
      setSaving(false);
      onChanged();
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-800"
        onClick={() => setOpen(true)}
      >
        <Plus className="h-4 w-4" aria-hidden />
        {t("ws_approvals_add_individual")}
      </button>
    );
  }

  return (
    <div className="mt-2 w-full rounded-md border border-gray-200 bg-gray-50 p-3">
      <p className="mb-2 text-xs text-gray-500">{t("ws_approvals_add_individual_hint")}</p>
      {roles === null ? (
        <p className="text-sm text-gray-500">{t("loading")}</p>
      ) : groups.length === 0 ? (
        <p className="text-sm text-gray-500">{t("ws_individual_nothing_to_add")}</p>
      ) : (
        <div className="max-h-64 space-y-3 overflow-y-auto">
          {groups.map(({ area, roles: areaRoles }) => (
            <div key={area.id}>
              {managedAreas.length > 1 && (
                <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">{area.name}</p>
              )}
              <GroupedRoleCheckboxes
                roles={areaRoles}
                checkedRoleIds={selected}
                onToggleRole={(roleId) =>
                  setSelected((prev) =>
                    prev.includes(roleId) ? prev.filter((id) => id !== roleId) : [...prev, roleId]
                  )
                }
                onSetCheckedRoleIds={setSelected}
              />
            </div>
          ))}
        </div>
      )}
      <div className="mt-3 flex justify-end gap-2">
        <button
          type="button"
          onClick={() => {
            setSelected([]);
            setOpen(false);
          }}
          disabled={saving}
          className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
        >
          {t("cancel")}
        </button>
        <button
          type="button"
          onClick={save}
          disabled={saving || selected.length === 0}
          className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {t("ws_individual_add_action")}
        </button>
      </div>
    </div>
  );
}
